import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useAuth0 } from "@auth0/auth0-react"
import { useDispatch } from "react-redux"
import axios from "axios"
import _ from "lodash"
import store from "../store"
import {
	setIsEditing,
	resetAllEdits,
	setBaselineToCurrent
} from "../features/schedule/scheduleSlice"
import {
	resetAllEditsTomorrow,
	setTomorrowBaselineToCurrent
} from "../features/schedule/tomorrowSlice"
import { filterPatientScheduleForEditedPatients } from "../utils/mongoUtils"
import useLogging from "./useLogging"

/**
 * Diff `current` against `baseline` by id field. Returns { upserts, removals }
 * where upserts are entries in `current` that differ from baseline (or are new),
 * and removals are ids present in baseline but missing from current.
 */
const diffById = (current, baseline, idKey) => {
	const safeBaseline = baseline ?? []
	const upserts = current.filter(curr => {
		const base = safeBaseline.find(b => b[idKey] === curr[idKey])
		return !base || !_.isEqual(curr, base)
	})
	const removals = safeBaseline
		.filter(base => !current.find(curr => curr[idKey] === base[idKey]))
		.map(base => base[idKey])
	return { upserts, removals }
}

/**
 * React Query useMutation wrapper for the schedule patch endpoint. Makes the
 * UI feel instant: onMutate clears `isEditing` synchronously (before the
 * network round-trip), so the modal/spinner unblocks immediately. Redux
 * already holds the optimistic state (set by the edit reducers before save),
 * so there's no separate cache write here.
 *
 * Payload optimization: patients are diff-tracked via `editedPatients` flags,
 * while nurses and chairs are diff'd against `nurseScheduleBaseline` /
 * `chairScheduleBaseline` (pure server truth maintained by setOptimizeData).
 * After a successful save, baseline is reset to current via
 * setBaselineToCurrent so the next save's diff stays minimal.
 *
 * On error: invalidates the schedule query. The refetch returns the canonical
 * server state, which `setOptimizeData` writes into Redux — that's the
 * rollback. On success: clears edit flags + resets baseline + success toast.
 */
export const useSchedulePatch = ({ isTomorrowMode = false, sandbox = false } = {}) => {
	const { getAccessTokenSilently } = useAuth0()
	const dispatch = useDispatch()
	const queryClient = useQueryClient()
	const logging = useLogging()

	return useMutation({
		mutationFn: async ({ date }) => {
			if (sandbox) return { skipped: true }

			const token = await getAccessTokenSilently({
				audience: process.env.REACT_APP_API_IDENTIFIER
			})

			const currentState = isTomorrowMode
				? store.getState().tomorrow
				: store.getState().schedule

			const patientUpserts = filterPatientScheduleForEditedPatients(
				currentState.patientSchedule,
				currentState.editedPatients
			)
			const patientRemovals = (currentState.removedPatients || []).map(p =>
				typeof p === "object" ? p.patientId : p
			)

			// Reindex nurses (server expects sequential `id` field per array
			// position), then diff against baseline.
			const reindexedNurses = currentState.nurseSchedule.map((nurse, i) => ({
				...nurse,
				id: i
			}))
			const { upserts: nurseUpserts, removals: nurseRemovals } = diffById(
				reindexedNurses,
				currentState.nurseScheduleBaseline,
				"nurseId"
			)
			const { upserts: chairUpserts, removals: chairRemovals } = diffById(
				currentState.chairSchedule,
				currentState.chairScheduleBaseline,
				"chairId"
			)

			const requestBody = {
				id: currentState.documentId ?? undefined,
				date,
				patientUpserts,
				patientRemovals,
				nurseUpserts,
				nurseRemovals,
				chairUpserts,
				chairRemovals,
				chargeNurse: currentState.chargeNurse
			}

			// Clear edit flags now that the diff has been captured into requestBody.
			// Must happen after filterPatientScheduleForEditedPatients above —
			// otherwise patientUpserts comes back empty and saves silently no-op.
			if (isTomorrowMode) {
				dispatch(resetAllEditsTomorrow())
			} else {
				dispatch(resetAllEdits())
			}

			const headers = {
				Authorization: `Bearer ${token}`,
				"Content-Type": "application/json"
			}

			try {
				await axios.post(
					`${process.env.REACT_APP_BACKEND_URL}/api/patch-schedule`,
					requestBody,
					{ headers }
				)
				return {
					patientUpserts: patientUpserts.length,
					patientRemovals: patientRemovals.length,
					nurseUpserts: nurseUpserts.length,
					nurseRemovals: nurseRemovals.length,
					chairUpserts: chairUpserts.length,
					chairRemovals: chairRemovals.length
				}
			} catch (error) {
				// Patch only works when a schedule already exists for the date.
				// On 404, fall back to the legacy full-object endpoint, which
				// creates the document if missing.
				if (error?.response?.status !== 404) throw error

				logging.info("useSchedulePatch - no existing schedule, falling back to /api/update-schedule")
				await axios.post(
					`${process.env.REACT_APP_BACKEND_URL}/api/update-schedule`,
					{
						id: currentState.documentId ?? undefined,
						date,
						patientSchedule: currentState.patientSchedule,
						nurseSchedule: reindexedNurses,
						chairSchedule: currentState.chairSchedule,
						chargeNurse: currentState.chargeNurse
					},
					{ headers }
				)
				return { fallback: true }
			}
		},
		onMutate: () => {
			dispatch(setIsEditing(false))
		},
		onSuccess: (data, { setNotification }) => {
			if (data?.skipped) return
			// Baseline update stays in onSuccess — it's an internal
			// optimization, and on failure the refetch's setOptimizeData
			// re-syncs the baseline from the server payload anyway.
			if (isTomorrowMode) {
				dispatch(setTomorrowBaselineToCurrent())
			} else {
				dispatch(setBaselineToCurrent())
			}
			logging.info("useSchedulePatch - save succeeded", data)
			if (setNotification) {
				setNotification({ visible: true, message: "Changes saved.", success: true })
				setTimeout(() => setNotification({ visible: false }), 2000)
			}
		},
		onError: (err, { setNotification }) => {
			logging.error("useSchedulePatch - save failed", err)
			const queryKey = isTomorrowMode ? ["scheduleTomorrow"] : ["schedule"]
			// Refetch from server → setOptimizeData overwrites Redux → rollback.
			queryClient.invalidateQueries({ queryKey })
			if (setNotification) {
				setNotification({
					visible: true,
					message: "Save failed — reloading latest data.",
					success: false
				})
				setTimeout(() => setNotification({ visible: false }), 3000)
			}
		}
	})
}
